import React, { useState, useMemo, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  Tabs,
  Text,
  Badge,
  Button,
  Card,
  Table,
  StatsGrid,
  StatusDot,
  VersionTimeline,
} from '@teals/ui-web';
import type { TabItem, Column, VersionEntry, StatItem } from '@teals/ui-web';
import {
  useGetAgentQuery,
  useUpdateAgentConfigMutation,
  usePublishAgentMutation,
  useSendMessageMutation,
} from '../../store/api';

type DetailTab = 'overview' | 'configure' | 'playground';

interface AgentConfig {
  systemPrompt: string;
  model: string;
  temperature: number;
}

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
}

const TABS: TabItem[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'configure', label: 'Configure' },
  { id: 'playground', label: 'Playground' },
];

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 1200px;
`;

const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  background: none;
  border: none;
  color: var(--accent);
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: var(--accent-hover);
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const TabContent = styled.div`
  margin-top: 0.5rem;
`;

const OverviewSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const SectionLabel = styled.h3`
  font-size: 0.8125rem;
  font-weight: 600;
  color: var(--text-secondary);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0 0 0.75rem;
`;

const TwoColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;

  @media (max-width: 767px) {
    grid-template-columns: 1fr;
  }
`;

const ConfigPanel = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  align-items: start;

  @media (max-width: 1023px) {
    grid-template-columns: 1fr;
  }
`;

const EditorPane = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 180px;
  padding: 0.75rem 1rem;
  border: 2px solid var(--border);
  border-radius: 0.5rem;
  font-family: inherit;
  font-size: 0.875rem;
  color: var(--text-primary);
  background: var(--bg-surface);
  resize: vertical;
  outline: none;
  line-height: 1.6;

  &:focus {
    border-color: var(--accent);
  }

  &:disabled {
    opacity: 0.6;
  }
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--text-primary);
  margin-bottom: 0.25rem;
`;

const Select = styled.select`
  width: 100%;
  padding: 0.5rem 0.75rem;
  min-height: 44px;
  border: 2px solid var(--border);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-primary);
  background: var(--bg-surface);
  outline: none;

  &:focus {
    border-color: var(--accent);
  }
`;

const SliderWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.375rem;
`;

const SliderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const Slider = styled.input`
  flex: 1;
  accent-color: var(--accent);
`;

const SliderValue = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--text-primary);
  min-width: 2.5rem;
  text-align: right;
`;

const PreviewCard = styled(Card)`
  position: sticky;
  top: 1rem;
`;

const JsonPre = styled.pre`
  margin: 0;
  font-family: 'SF Mono', 'Fira Code', 'Cascadia Code', monospace;
  font-size: 0.8125rem;
  color: var(--text-primary);
  line-height: 1.6;
  overflow-x: auto;
  white-space: pre-wrap;
  word-break: break-word;
`;

const ConfigActions = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 500px;
  border: 1px solid var(--border);
  border-radius: 0.75rem;
  overflow: hidden;
  background: var(--bg-surface);
`;

const ChatBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem 1rem;
  background: var(--bg-elevated, #f8fafc);
  border-bottom: 1px solid var(--border);
  font-size: 0.8125rem;
  color: var(--text-secondary);
`;

const MessageList = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const MessageBubble = styled.div<{ $role: 'user' | 'assistant' }>`
  max-width: 75%;
  padding: 0.75rem 1rem;
  border-radius: 0.75rem;
  font-size: 0.875rem;
  line-height: 1.5;
  align-self: ${({ $role }) => ($role === 'user' ? 'flex-end' : 'flex-start')};
  background: ${({ $role }) =>
    $role === 'user' ? 'var(--accent)' : 'var(--bg-elevated, #f1f5f9)'};
  color: ${({ $role }) => ($role === 'user' ? 'white' : 'var(--text-primary)')};
`;

const ChatInputRow = styled.form`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-top: 1px solid var(--border);
`;

const ChatInput = styled.input`
  flex: 1;
  padding: 0.625rem 0.875rem;
  border: 1px solid var(--border);
  border-radius: 0.5rem;
  font-size: 0.875rem;
  color: var(--text-primary);
  background: transparent;
  outline: none;

  &:focus {
    border-color: var(--accent);
  }
`;

const EmptyState = styled.div`
  padding: 3rem 1rem;
  text-align: center;
  color: var(--text-muted);
`;

const ErrorBanner = styled.div`
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
  background: color-mix(in srgb, var(--danger, #dc2626) 12%, transparent);
  color: var(--danger, #dc2626);
  font-size: 0.875rem;
`;

const SCOPE_BADGES: Record<
  string,
  { variant: 'primary' | 'info' | 'warning'; label: string }
> = {
  global: { variant: 'primary', label: 'Global' },
  organisation: { variant: 'info', label: 'Organisation' },
  application: { variant: 'warning', label: 'Application' },
};

const AgentDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = (searchParams.get('tab') as DetailTab) || 'overview';
  const [activeTab, setActiveTab] = useState<DetailTab>(
    ['overview', 'configure', 'playground'].includes(initialTab)
      ? initialTab
      : 'overview',
  );
  const [config, setConfig] = useState<AgentConfig>({
    systemPrompt: '',
    model: 'gpt-4o',
    temperature: 0.7,
  });
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [sessionId, setSessionId] = useState<string | undefined>();
  const [actionError, setActionError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: agent, isLoading, isError } = useGetAgentQuery(id, {
    skip: !id || id === 'new',
  });
  const [updateConfig] = useUpdateAgentConfigMutation();
  const [publishAgent] = usePublishAgentMutation();
  const [sendMessage, { isLoading: isSending }] = useSendMessageMutation();

  const readOnly = Boolean(agent?.readOnly || agent?.scope === 'global');

  useEffect(() => {
    if (agent?.config) {
      setConfig({
        systemPrompt: agent.config.systemPrompt || '',
        model: agent.config.model || 'gpt-4o',
        temperature: agent.config.temperature ?? 0.7,
      });
    }
  }, [agent?.id, agent?.config]);

  useEffect(() => {
    if (agent) {
      setMessages([
        {
          id: 'greeting',
          role: 'assistant',
          content: `Hello! I'm ${agent.name}. How can I help you today?`,
        },
      ]);
      setSessionId(undefined);
    }
  }, [agent?.id, agent?.name]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleTabChange = (tab: string) => {
    const next = tab as DetailTab;
    setActiveTab(next);
    setSearchParams(next === 'overview' ? {} : { tab: next });
  };

  const handleSaveDraft = async () => {
    if (!agent || readOnly) return;
    setSaving(true);
    setActionError(null);
    try {
      await updateConfig({
        id: agent.id,
        config: {
          system_prompt: config.systemPrompt,
          model: config.model,
          temperature: config.temperature,
        },
      }).unwrap();
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to save draft',
      );
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async () => {
    if (!agent || readOnly) return;
    setSaving(true);
    setActionError(null);
    try {
      await updateConfig({
        id: agent.id,
        config: {
          system_prompt: config.systemPrompt,
          model: config.model,
          temperature: config.temperature,
        },
      }).unwrap();
      await publishAgent(agent.id).unwrap();
    } catch (err) {
      setActionError(
        err instanceof Error ? err.message : 'Failed to publish agent',
      );
    } finally {
      setSaving(false);
    }
  };

  const handleSendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!chatInput.trim() || !agent) return;

    const userMsg: ChatMessage = {
      id: `msg-${Date.now()}`,
      role: 'user',
      content: chatInput.trim(),
    };
    setMessages((prev) => [...prev, userMsg]);
    setChatInput('');
    setActionError(null);

    try {
      const result = await sendMessage({
        query: userMsg.content,
        agentName: agent.agentName,
        sessionId,
      }).unwrap();
      if (result.sessionId) setSessionId(result.sessionId);
      setMessages((prev) => [
        ...prev,
        {
          id: result.exchangeId || `msg-${Date.now() + 1}`,
          role: 'assistant',
          content:
            result.response ||
            (result.isAsync
              ? 'Processing… (async response). Check the inspector or wait for WebSocket events.'
              : 'No response returned.'),
        },
      ]);
    } catch {
      setMessages((prev) => [
        ...prev,
        {
          id: `msg-${Date.now() + 1}`,
          role: 'assistant',
          content:
            'Unable to reach the chat API. Ensure the agent is published or available for sandbox testing.',
        },
      ]);
    }
  };

  const configJson = useMemo(
    () =>
      JSON.stringify(
        {
          agent_id: agent?.id,
          agent_name: agent?.agentName,
          system_prompt: config.systemPrompt,
          model: config.model,
          temperature: config.temperature,
        },
        null,
        2,
      ),
    [agent?.id, agent?.agentName, config],
  );

  const stats: StatItem[] = useMemo(() => {
    if (!agent) return [];
    return [
      {
        label: 'Total Sessions',
        value: String(agent.stats.totalSessions.toLocaleString()),
      },
      {
        label: 'Exchanges',
        value: String(agent.stats.totalExchanges.toLocaleString()),
      },
      {
        label: 'Avg Response Time',
        value:
          agent.stats.avgResponseTime > 0
            ? `${agent.stats.avgResponseTime}s`
            : '—',
      },
      {
        label: 'Active Applications',
        value: String(agent.stats.activeApplications),
      },
    ];
  }, [agent]);

  const timelineVersions: VersionEntry[] = useMemo(() => {
    if (!agent) return [];
    return agent.versions.map((v) => ({
      version: v.version,
      status: v.status,
      date: v.date,
      description: v.description,
      changeCount: v.changeCount,
    }));
  }, [agent]);

  const applicationColumns: Column<{
    id: string;
    name: string;
    status: string;
    appId: string;
  }>[] = useMemo(
    () => [
      { key: 'name', header: 'Application' },
      {
        key: 'status',
        header: 'Status',
        render: (val: string) => (
          <Badge
            variant={
              String(val).toLowerCase() === 'active' ? 'success' : 'neutral'
            }
            size="sm"
          >
            {val}
          </Badge>
        ),
      },
      { key: 'appId', header: 'App ID' },
    ],
    [],
  );

  if (!id || id === 'new') {
    return (
      <Page>
        <BackLink onClick={() => navigate('/agents')}>
          &larr; Back to Agents
        </BackLink>
        <EmptyState>
          Use Create Agent on the Agents page to start a new draft.
        </EmptyState>
      </Page>
    );
  }

  if (isLoading) {
    return (
      <Page>
        <EmptyState>Loading agent…</EmptyState>
      </Page>
    );
  }

  if (isError || !agent) {
    return (
      <Page>
        <BackLink onClick={() => navigate('/agents')}>
          &larr; Back to Agents
        </BackLink>
        <EmptyState>Agent not found.</EmptyState>
      </Page>
    );
  }

  const scopeCfg = SCOPE_BADGES[agent.scope] ?? SCOPE_BADGES.global;

  return (
    <Page>
      <BackLink onClick={() => navigate('/agents')}>
        &larr; Back to Agents
      </BackLink>

      <HeaderRow>
        <Text variant="h3">{agent.name}</Text>
        <StatusDot status={agent.versionStatus} label={agent.versionStatus} />
        <Badge variant={scopeCfg.variant} size="sm">
          {scopeCfg.label}
        </Badge>
        {readOnly && (
          <Badge variant="neutral" size="sm">
            Read-only
          </Badge>
        )}
      </HeaderRow>

      {agent.description && (
        <Text variant="body1" style={{ color: 'var(--text-secondary)', margin: 0 }}>
          {agent.description}
        </Text>
      )}

      {actionError && <ErrorBanner>{actionError}</ErrorBanner>}

      <Tabs
        items={TABS}
        activeTab={activeTab}
        onChange={handleTabChange}
        variant="underline"
      />

      <TabContent>
        {activeTab === 'overview' && (
          <OverviewSection>
            <div>
              <SectionLabel>Performance</SectionLabel>
              <StatsGrid items={stats} columns={4} />
            </div>

            <TwoColumn>
              <div>
                <SectionLabel>Version History</SectionLabel>
                {timelineVersions.length > 0 ? (
                  <VersionTimeline versions={timelineVersions} />
                ) : (
                  <Text variant="body1" style={{ color: 'var(--text-muted)' }}>
                    No version history yet.
                  </Text>
                )}
              </div>
              <div>
                <SectionLabel>Associated Applications</SectionLabel>
                {agent.applications.length > 0 ? (
                  <Table
                    columns={applicationColumns}
                    data={agent.applications}
                    rowKey="id"
                  />
                ) : (
                  <Text variant="body1" style={{ color: 'var(--text-muted)' }}>
                    No applications associated.
                  </Text>
                )}
              </div>
            </TwoColumn>
          </OverviewSection>
        )}

        {activeTab === 'configure' && (
          <ConfigPanel>
            <EditorPane>
              <div>
                <FieldLabel>System Prompt</FieldLabel>
                <TextArea
                  value={config.systemPrompt}
                  disabled={readOnly}
                  onChange={(e) =>
                    setConfig((c) => ({ ...c, systemPrompt: e.target.value }))
                  }
                />
              </div>

              <div>
                <FieldLabel>Model</FieldLabel>
                <Select
                  value={config.model}
                  disabled={readOnly}
                  onChange={(e) =>
                    setConfig((c) => ({ ...c, model: e.target.value }))
                  }
                >
                  <option value="gpt-4o">GPT-4o</option>
                  <option value="gpt-4o-mini">GPT-4o Mini</option>
                  <option value="gpt-4.1-mini">GPT-4.1 Mini</option>
                  <option value="claude-sonnet-4-20250514">Claude Sonnet 4</option>
                  <option value="claude-3-5-haiku-20241022">Claude 3.5 Haiku</option>
                </Select>
              </div>

              <SliderWrapper>
                <FieldLabel>Temperature</FieldLabel>
                <SliderRow>
                  <Slider
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={config.temperature}
                    disabled={readOnly}
                    onChange={(e) =>
                      setConfig((c) => ({
                        ...c,
                        temperature: parseFloat(e.target.value),
                      }))
                    }
                  />
                  <SliderValue>{config.temperature.toFixed(1)}</SliderValue>
                </SliderRow>
              </SliderWrapper>

              {!readOnly && (
                <ConfigActions>
                  <Button
                    variant="outline"
                    onClick={handleSaveDraft}
                    disabled={saving}
                  >
                    {saving ? 'Saving…' : 'Save Draft'}
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handlePublish}
                    disabled={saving}
                  >
                    Publish
                  </Button>
                </ConfigActions>
              )}
            </EditorPane>

            <PreviewCard padding={20} elevation="sm">
              <SectionLabel>Configuration Preview</SectionLabel>
              <JsonPre>{configJson}</JsonPre>
            </PreviewCard>
          </ConfigPanel>
        )}

        {activeTab === 'playground' && (
          <ChatContainer>
            <ChatBanner>
              <StatusDot status="info" size="sm" />
              {agent.versionStatus === 'draft'
                ? 'Draft agents may not be available for chat until published'
                : `Sandbox chat · ${agent.agentName}`}
            </ChatBanner>
            <MessageList>
              {messages.map((msg) => (
                <MessageBubble key={msg.id} $role={msg.role}>
                  {msg.content}
                </MessageBubble>
              ))}
              <div ref={messagesEndRef} />
            </MessageList>
            <ChatInputRow onSubmit={handleSendMessage}>
              <ChatInput
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Type a message..."
                disabled={isSending}
              />
              <Button
                type="submit"
                variant="primary"
                size="sm"
                disabled={!chatInput.trim() || isSending}
              >
                Send
              </Button>
            </ChatInputRow>
          </ChatContainer>
        )}
      </TabContent>
    </Page>
  );
};

export default AgentDetail;
